import {useState, useEffect} from "react";
import {
    MicrophoneIcon, CalendarDaysIcon, PencilSquareIcon, TrashIcon,
    CheckCircleIcon, VideoCameraIcon, ChartBarIcon, DocumentTextIcon, SparklesIcon,
} from "@heroicons/react/24/outline";
import Spinner from "../components/Spinner";

interface ReunionPrevue {
    id: number; titre: string; date: string; participants: string[];
    templateId: number; statut: "planifiee" | "en_cours" | "terminee"; lienMeet: string;
}
interface Transcription { id: number; titreReunion: string; progression: number; }
interface Template { id: number; nom: string; }

export default function Tableau() {
    const [reunions, setReunions] = useState<ReunionPrevue[]>([]);
    const [transcriptions, setTranscriptions] = useState<Transcription[]>([]);
    const [templates, setTemplates] = useState<Template[]>([]);
    const [chargement, setChargement] = useState(true);
    const [erreur, setErreur] = useState("");
    const [succes, setSucces] = useState("");
    const [reunionSelectionnee, setReunionSelectionnee] = useState<ReunionPrevue | null>(null);
    const [nouveauTemplateId, setNouveauTemplateId] = useState<number | null>(null);
    const [statsArchives, setStatsArchives] = useState(0);

    const recupererDonnees = async () => {
        try {
            const [repReunions, repTranscriptions, repTemplates, repArchives] = await Promise.all([
                fetch(`${process.env.REACT_APP_API_URL}/api/reunions`,
                    { credentials: "include", headers: { "Content-Type": "application/json" } }),
                fetch(`${process.env.REACT_APP_API_URL}/api/transcriptions/en-cours`,
                    { credentials: "include", headers: { "Content-Type": "application/json" } }),
                fetch(`${process.env.REACT_APP_API_URL}/api/templates`,
                    { credentials: "include", headers: { "Content-Type": "application/json" } }),
                fetch(`${process.env.REACT_APP_API_URL}/api/reunions/archives`,
                    { credentials: "include", headers: { "Content-Type": "application/json" } }),
            ]);
            if (!repReunions.ok) throw new Error();
            setReunions(await repReunions.json());
            if (repTranscriptions.ok) setTranscriptions(await repTranscriptions.json());
            if (repTemplates.ok) setTemplates(await repTemplates.json());
            if (repArchives.ok) {
                const archives = await repArchives.json();
                setStatsArchives(archives.length);
            }
        } catch {
            setErreur("Impossible de charger les données");
        } finally {
            setChargement(false);
        }
    };

    useEffect(() => {
        recupererDonnees();
        const intervalle = setInterval(recupererDonnees, 5000);
        return () => clearInterval(intervalle);
    }, []);

    const supprimerReunion = async (id: number) => {
        if (!window.confirm("Supprimer cette réunion ?")) return;
        try {
            const reponse = await fetch(`${process.env.REACT_APP_API_URL}/api/reunions/${id}`,
                { method: "DELETE", credentials: "include" });
            if (!reponse.ok) throw new Error();
            setReunions(prev => prev.filter(r => r.id !== id));
            setSucces("Réunion supprimée");
            setTimeout(() => setSucces(""), 3000);
        } catch { setErreur("Impossible de supprimer la réunion"); }
    };

    const terminerReunion = async (id: number) => {
        if (!window.confirm("Terminer cette réunion ? Elle sera déplacée dans les archives.")) return;
        try {
            const reponse = await fetch(`${process.env.REACT_APP_API_URL}/api/reunions/${id}/terminer`,
                { method: "PUT", credentials: "include" });
            if (!reponse.ok) throw new Error();
            setReunions(prev => prev.filter(r => r.id !== id));
            setSucces("Réunion terminée — elle est maintenant dans les Archives");
            setTimeout(() => setSucces(""), 4000);
        } catch { setErreur("Impossible de terminer la réunion"); }
    };

    const ouvrirModification = (reunion: ReunionPrevue) => {
        setReunionSelectionnee(reunion);
        setNouveauTemplateId(reunion.templateId);
    };

    const sauvegarderModification = async () => {
        if (!reunionSelectionnee) return;
        try {
            const reponse = await fetch(
                `${process.env.REACT_APP_API_URL}/api/reunions/${reunionSelectionnee.id}`,
                {
                    method: "PUT",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ ...reunionSelectionnee, templateId: nouveauTemplateId }),
                }
            );
            if (!reponse.ok) throw new Error();
            setReunions(prev => prev.map(r =>
                r.id === reunionSelectionnee.id
                    ? { ...r, templateId: nouveauTemplateId ?? r.templateId }
                    : r
            ));
            setReunionSelectionnee(null);
            setSucces("Réunion modifiée");
            setTimeout(() => setSucces(""), 3000);
        } catch { setErreur("Impossible de modifier la réunion"); }
    };

    const statutBadge = (s: string) =>
        s === "planifiee" ? "badge-info" : s === "en_cours" ? "badge-warning" : "badge-success";
    const statutLabel = (s: string) =>
        s === "planifiee" ? "Planifiée" : s === "en_cours" ? "En cours" : "Terminée";

    const stats = [
        {
            label: "Réunions planifiées",
            valeur: reunions.length,
            icon: <CalendarDaysIcon className="w-6 h-6" />,
            couleur: "#6B21A8",
            bg: "#F3E8FF",
        },
        {
            label: "Réunions archivées",
            valeur: statsArchives,
            icon: <ChartBarIcon className="w-6 h-6" />,
            couleur: "#F97316",
            bg: "#FFF7ED",
        },
        {
            label: "Transcriptions en cours",
            valeur: transcriptions.length,
            icon: <SparklesIcon className="w-6 h-6" />,
            couleur: "#0EA5E9",
            bg: "#E0F2FE",
        },
        {
            label: "Templates disponibles",
            valeur: templates.length,
            icon: <DocumentTextIcon className="w-6 h-6" />,
            couleur: "#10B981",
            bg: "#D1FAE5",
        },
    ];

    return (
        <div className="space-y-8">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800">Tableau de bord</h1>
                    <p className="text-gray-500 mt-1">Bienvenue sur GMITIX</p>
                </div>
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white font-bold text-lg"
                    style={{ background: "linear-gradient(135deg, #6B21A8, #F97316)" }}>G</div>
            </div>

            {erreur && <div className="alert alert-error rounded-xl"><span>{erreur}</span></div>}
            {succes && <div className="alert alert-success rounded-xl"><span>{succes}</span></div>}

            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                {stats.map((stat, i) => (
                    <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                            style={{ background: stat.bg, color: stat.couleur }}>
                            {stat.icon}
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-gray-800">{stat.valeur}</p>
                            <p className="text-xs text-gray-400 mt-0.5">{stat.label}</p>
                        </div>
                    </div>
                ))}
            </div>

            {chargement ? (
                <div className="flex justify-center items-center h-64">
                    <Spinner size="lg" className="text-purple-600" />
                </div>
            ) : (
                <>
                    {transcriptions.length > 0 && (
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-2 h-6 rounded-full"
                                    style={{ background: "linear-gradient(#6B21A8, #F97316)" }}></div>
                                <h2 className="text-xl font-bold text-gray-800">Transcriptions en cours</h2>
                            </div>
                            <div className="space-y-4">
                                {transcriptions.map(t => (
                                    <div key={t.id} className="bg-orange-50 border border-orange-100 rounded-xl p-4">
                                        <div className="flex items-center justify-between">
                                            <span className="font-medium text-gray-700">{t.titreReunion}</span>
                                            <div className="flex items-center gap-2 text-orange-500">
                                                <Spinner size="sm" />
                                                <span className="text-sm font-medium">Transcription en cours...</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-2 h-6 rounded-full"
                                style={{ background: "linear-gradient(#6B21A8, #F97316)" }}></div>
                            <h2 className="text-xl font-bold text-gray-800">Réunions prévues</h2>
                        </div>
                        {reunions.length === 0 ? (
                            <div className="text-center py-12">
                                <CalendarDaysIcon className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                                <p className="text-gray-400 font-medium">Aucune réunion prévue</p>
                                <p className="text-gray-300 text-sm mt-1">Créez votre première réunion</p>
                                <a href="/nouvelle-reunion"
                                    className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-xl text-white text-sm font-medium"
                                    style={{ background: "linear-gradient(90deg, #6B21A8, #F97316)" }}>
                                    <MicrophoneIcon className="w-4 h-4" />Nouvelle réunion
                                </a>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-gray-100">
                                            <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Titre</th>
                                            <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Date</th>
                                            <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Participants</th>
                                            <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Statut</th>
                                            <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Meet</th>
                                            <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reunions.map(reunion => (
                                            <tr key={reunion.id}
                                                className="border-b border-gray-50 hover:bg-purple-50/30 transition-colors">
                                                <td className="py-4 px-4 font-medium text-gray-800">{reunion.titre}</td>
                                                <td className="py-4 px-4 text-gray-600 text-sm">
                                                    {new Date(reunion.date).toLocaleDateString("fr-FR", {
                                                        day: "2-digit", month: "short", year: "numeric"
                                                    })}
                                                </td>
                                                <td className="py-4 px-4">
                                                    <span className="bg-purple-100 text-purple-700 text-xs font-medium px-2 py-1 rounded-lg">
                                                        {reunion.participants.length} participant(s)
                                                    </span>
                                                </td>
                                                <td className="py-4 px-4">
                                                    <span className={`badge ${statutBadge(reunion.statut)}`}>
                                                        {statutLabel(reunion.statut)}
                                                    </span>
                                                </td>
                                                <td className="py-4 px-4">
                                                    {reunion.lienMeet ? (
                                                        <a href={reunion.lienMeet} target="_blank" rel="noreferrer"
                                                            className="btn btn-sm rounded-xl text-blue-500 border-blue-200 hover:bg-blue-50 gap-1">
                                                            <VideoCameraIcon className="w-4 h-4" />Rejoindre
                                                        </a>
                                                    ) : (
                                                        <span className="text-gray-300 text-sm">—</span>
                                                    )}
                                                </td>
                                                <td className="py-4 px-4">
                                                    <div className="flex gap-2">
                                                        <button className="btn btn-sm rounded-xl text-green-600 border-green-200 hover:bg-green-50 gap-1"
                                                            onClick={() => terminerReunion(reunion.id)}>
                                                            <CheckCircleIcon className="w-4 h-4" />Terminer
                                                        </button>
                                                        <button className="btn btn-sm rounded-xl text-purple-600 border-purple-200 hover:bg-purple-50 gap-1"
                                                            onClick={() => ouvrirModification(reunion)}>
                                                            <PencilSquareIcon className="w-4 h-4" />Modifier
                                                        </button>
                                                        <button className="btn btn-sm rounded-xl text-red-500 border-red-200 hover:bg-red-50 gap-1"
                                                            onClick={() => supprimerReunion(reunion.id)}>
                                                            <TrashIcon className="w-4 h-4" />Supprimer
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </>
            )}

            {reunionSelectionnee && (
                <div className="fixed inset-0 flex items-center justify-center"
                    style={{ zIndex: 9999, backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="bg-white max-w-lg w-full mx-4 rounded-2xl p-6 shadow-2xl">
                        <h3 className="font-bold text-lg mb-1">Modifier la réunion</h3>
                        <p className="text-gray-500 text-sm mb-6">{reunionSelectionnee.titre}</p>
                        <div className="form-control mb-4">
                            <label className="label pb-1">
                                <span className="label-text font-medium">Template</span>
                            </label>
                            <select className="select select-bordered w-full rounded-xl"
                                value={nouveauTemplateId ?? ""}
                                onChange={e => setNouveauTemplateId(Number(e.target.value))}>
                                <option value="">Sélectionnez un template</option>
                                {templates.map(t => <option key={t.id} value={t.id}>{t.nom}</option>)}
                            </select>
                        </div>
                        <div className="flex justify-between pt-4 border-t border-gray-100">
                            <button className="btn btn-outline rounded-xl"
                                onClick={() => setReunionSelectionnee(null)}>Fermer</button>
                            <button className="btn text-white rounded-xl"
                                style={{ background: "linear-gradient(90deg, #6B21A8, #F97316)" }}
                                onClick={sauvegarderModification}>Sauvegarder</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
